import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Link2, Loader2, TrendingDown, TrendingUp, X } from 'lucide-react';
import { formatMoeda, formatDataHoraSP } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../Toast';
import { useAviso } from '../Aviso';
import { Modal } from '../Modal';
import { MoneyInput } from '../inputs/MoneyInput';
import { SaldoInsuficienteModal, lerSaldoInsuficiente, type SaldoInsuficiente } from '../../modules/caixa/SaldoInsuficienteModal';

// Sangria, Suprimento e Transferencia vivem AQUI, nao na tela, porque duas telas abrem as
// mesmas portas de dinheiro: a aba Sessoes do /caixa e a tela /financeiro. Duplicar os
// formularios deixaria as duas divergirem com o tempo — e divergencia em porta de dinheiro
// vira regra diferente dependendo de onde o operador clicou.
//
// A trava anti-negativo (DA-342) mora no BACKEND; aqui so traduzimos o 409 no modal
// bloqueante, que e o comportamento que o Ruan pediu (dinheiro recusado nao passa em toast).

export type ContaOpcao = { id: number; nome: string; tipo: string };

const rotuloCampo: React.CSSProperties = { fontSize: 12, fontWeight: 600 };
const campo: React.CSSProperties = { width: '100%', padding: '6px 8px', borderRadius: 4, fontSize: 13 };

/**
 * Guarda cedula? Gaveta (tipo 'dinheiro') e o Cofre — que e `tipo='banco'` no cadastro e so se
 * identifica pelo NOME. So quem guarda cedula pode ser ORIGEM de transferencia.
 */
export function podeSerOrigemTransferencia(c: ContaOpcao): boolean {
  return c.tipo === 'dinheiro' || c.nome === 'Cofre';
}

/** Caixas que guardam cedula (gaveta + Cofre) — os unicos que podem ser ORIGEM de uma transferencia. */
function contasDeEspecie(contas: ContaOpcao[]): ContaOpcao[] {
  return contas.filter(podeSerOrigemTransferencia);
}

/** Conta que nao guarda cedula: banco, digital, maquininha. */
function ehContaBancaria(c: ContaOpcao): boolean {
  return !podeSerOrigemTransferencia(c);
}

/**
 * Espelha `modoTransferencia` do backend (caixa-transito.service.ts) — o servidor e quem MANDA;
 * aqui e so pra tela saber o que dizer antes de enviar. Qualquer perna em especie = alguem carrega
 * dinheiro = em transito. Duas contas bancarias = movimento eletronico = imediata.
 */
function ehImediata(origem: ContaOpcao | undefined, destino: ContaOpcao | undefined): boolean {
  if (!origem || !destino) return false;
  return ehContaBancaria(origem) && ehContaBancaria(destino);
}

/**
 * Entrada e saida de dinheiro numa conta — a porta unica que substituiu os botoes separados
 * "Sangria" e "Suprimento" (pedido do Ruan, 15/08/2026).
 *
 * Por que fundir: os dois SEMPRE foram o mesmo formulario (mesma rota, mesmos campos, mesma
 * trava) e so diferiam no `tipo` que o botao mandava — duas portas na tela pra uma peca so.
 *
 * Por que o rotulo mantem as duas palavras (`Entrada (suprimento)` / `Saida (sangria)`): o resto
 * do sistema fala sangria/suprimento — o fechamento de turno calcula
 * `esperado = abertura + recebido + suprimentos - sangrias`, e a aba Fluxo e o extrato exibem
 * esses termos. Nomear so "entrada/saida" aqui faria o operador registrar uma palavra e ler outra
 * na conferencia. As duas juntas custam tres caracteres e evitam vocabulario duplo.
 *
 * A direcao NAO tem default: e o campo que decide pra que lado o dinheiro anda, e default errado
 * numa tela de dinheiro e o modo caro de falhar. Sem escolha, o botao fica apagado dizendo o que
 * falta ("so libera com confirmacao positiva").
 */
export function ModalMovimentacaoCaixa({
  contas, contaInicial, onFechar, onConcluido,
}: {
  contas: ContaOpcao[];
  /** Conta ja marcada na tela de origem — evita reescolher o que o operador acabou de selecionar. */
  contaInicial?: number | null;
  onFechar: () => void;
  onConcluido: () => void;
}) {
  const toast = useToast();
  const aviso = useAviso();
  const [tipo, setTipo] = useState<'sangria' | 'suprimento' | ''>('');
  const [form, setForm] = useState<{ caixa_id: number | ''; valor: number; motivo: string }>({
    caixa_id: contaInicial ?? '',
    valor: 0,
    motivo: '',
  });
  const [saldoInsuf, setSaldoInsuf] = useState<SaldoInsuficiente | null>(null);
  const [salvando, setSalvando] = useState(false);
  // Saldo pelo numero da TRAVA anti-negativo (DA-342) — so importa na SAIDA, que e a unica que ela
  // recusa. Mesma leitura que o modal de Transferencia faz, pelo mesmo motivo: com o numero a
  // vista, "saldo insuficiente" para de chegar como surpresa depois do clique.
  const [saldo, setSaldo] = useState<number | null>(null);
  const [carregandoSaldo, setCarregandoSaldo] = useState(false);
  const reqSaldo = useRef(0);

  useEffect(() => {
    const id = form.caixa_id;
    if (!id || tipo !== 'sangria') { setSaldo(null); setCarregandoSaldo(false); return; }
    // Guarda de corrida: trocar de conta rapido lanca duas consultas, e a que responder por ultimo
    // nao e necessariamente a mais nova — a tela ficaria coerentemente errada.
    const req = ++reqSaldo.current;
    setCarregandoSaldo(true);
    api.get<{ saldo: number }>(`/api/caixa/saldo-para-saida/${id}`)
      .then((r) => { if (req === reqSaldo.current) { setSaldo(Number(r.saldo)); setCarregandoSaldo(false); } })
      .catch(() => { if (req === reqSaldo.current) { setSaldo(null); setCarregandoSaldo(false); } });
  }, [form.caixa_id, tipo]);

  const ehSaida = tipo === 'sangria';

  // O que ainda falta — `null` quando pode registrar. Vale pra DESABILITAR o botao e pra ESCREVER
  // ao lado dele o motivo: botao apagado e mudo e beco sem saida, nao interface limpa.
  const falta: string | null = (() => {
    if (!tipo) return 'Escolha se e entrada ou saida.';
    if (!form.caixa_id) return 'Escolha a conta.';
    if (!(form.valor > 0)) return 'Informe o valor.';
    if (ehSaida && saldo !== null && Math.round(form.valor * 100) > Math.round(saldo * 100)) {
      return `Esta conta tem ${formatMoeda(saldo)} — faltam ${formatMoeda(form.valor - saldo)}.`;
    }
    return null;
  })();

  async function confirmar() {
    if (!tipo || !form.caixa_id || form.valor <= 0) return;
    setSalvando(true);
    try {
      await api.post('/api/caixa/movimentacao', { tipo, ...form });
      toast.sucesso(ehSaida ? 'Saida registrada (sangria)' : 'Entrada registrada (suprimento)');
      onConcluido();
      onFechar();
    } catch (e: any) {
      const s = lerSaldoInsuficiente(e);
      if (s) { setSaldoInsuf(s); return; }
      aviso.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  /** Um lado do seletor de direcao. Escolhido = fundo solido `--primary`; o icone carrega o
   *  sentido sem depender de cor (daltonismo). */
  function BotaoDirecao({ valor, rotulo, Icone }: {
    valor: 'suprimento' | 'sangria'; rotulo: string; Icone: typeof TrendingUp;
  }) {
    const ativo = tipo === valor;
    return (
      <button
        type="button"
        onClick={() => setTipo(valor)}
        aria-pressed={ativo}
        style={{
          flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          padding: '9px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 13.5,
          whiteSpace: 'nowrap',
          border: `1px solid ${ativo ? 'var(--primary)' : 'var(--input-border)'}`,
          background: ativo ? 'var(--primary)' : 'var(--input-bg)',
          color: ativo ? '#fff' : 'var(--input-fg)',
          fontWeight: ativo ? 700 : 500,
        }}
      >
        <Icone size={15} /> {rotulo}
      </button>
    );
  }

  return (
    <>
      <Modal aberto onFechar={onFechar}
        titulo="Entrada / Saida no caixa"
        largura={460}
        rodape={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, opacity: 0.8, textAlign: 'left' }}>{falta ?? ''}</span>
            <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
              <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
              <button
                type="button"
                className="btn-primario"
                onClick={confirmar}
                disabled={salvando || falta !== null}
              >
                {ehSaida ? 'Registrar saida' : tipo ? 'Registrar entrada' : 'Registrar'}
              </button>
            </div>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={rotuloCampo}>Movimento</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
              <BotaoDirecao valor="suprimento" rotulo="Entrada (suprimento)" Icone={TrendingUp} />
              <BotaoDirecao valor="sangria" rotulo="Saida (sangria)" Icone={TrendingDown} />
            </div>
          </div>

          <div>
            <label style={rotuloCampo}>Caixa</label>
            <select
              value={form.caixa_id}
              onChange={(e) => setForm((s) => ({ ...s, caixa_id: e.target.value ? Number(e.target.value) : '' }))}
              style={campo}
            >
              <option value="">Selecione...</option>
              {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>

            {/* So na SAIDA: e a unica direcao que a trava recusa. Entrada nunca deixa saldo negativo. */}
            {ehSaida && form.caixa_id !== '' && (
              <div style={{ marginTop: 4, fontSize: 12 }}>
                {carregandoSaldo ? (
                  <span style={{ opacity: 0.6 }}>lendo o saldo...</span>
                ) : saldo === null ? (
                  <span style={{ opacity: 0.6 }}>saldo indisponivel</span>
                ) : saldo < 0 ? (
                  // Laranja porque e ATENCAO, nao erro (vermelho fica pro que quebrou; verde nunca
                  // — daltonismo). Fundo solido + letra preta, nao tint lavado. So aparece na conta
                  // que ESTA negativa, entao nao e alarme que grita atoa.
                  <span style={{
                    display: 'inline-block', background: '#f97316', color: '#000',
                    padding: '2px 6px', borderRadius: 3, fontSize: 11.5, lineHeight: 1.35,
                  }}>
                    conta negativa no sistema:{' '}
                    <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoeda(saldo)}</strong>
                    {' '}— nao da pra tirar dinheiro dela ate a entrada que falta ser lancada.
                  </span>
                ) : (
                  <span style={{ opacity: 0.85 }}>
                    disponivel <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoeda(saldo)}</strong>
                  </span>
                )}
              </div>
            )}
          </div>

          <div>
            <label style={rotuloCampo}>Valor</label>
            <MoneyInput valor={form.valor} onChangeValor={(v) => setForm((s) => ({ ...s, valor: v }))} style={{ width: '100%' }} />
          </div>

          <div>
            <label style={rotuloCampo}>Motivo</label>
            <textarea
              value={form.motivo}
              onChange={(e) => setForm((s) => ({ ...s, motivo: e.target.value }))}
              rows={3}
              style={{ ...campo, resize: 'vertical' }}
              placeholder={ehSaida ? 'Ex: troco, retirada, deposito' : 'Ex: aporte, deposito, saldo conferido no extrato'}
            />
          </div>
        </div>
      </Modal>
      {saldoInsuf && <SaldoInsuficienteModal info={saldoInsuf} onFechar={() => setSaldoInsuf(null)} />}
    </>
  );
}

/**
 * Pix da Conciliacao Bancaria que ainda pode ser amarrado a uma transferencia ([DA-549]).
 * A lista vem do backend com o MESMO recorte que ele aceita ao gravar — o seletor nunca oferece
 * o que o servidor recusaria.
 */
export interface PixParaVincular {
  id: number;
  valor: string;
  data_pagamento: string | null;
  criado_em: string;
  pagador_nome_exibicao: string | null;
  pagador_banco: string | null;
  pagador_agencia: string | null;
  pagador_conta: string | null;
}

/**
 * Seletor dos Pix pendentes. Modal PROPRIO (nao o `<Modal/>` da casa) porque abre de DENTRO de
 * outro modal: o `.modal-overlay` da casa e z-1000, e quem nasce ali dentro mora no andar de cima
 * (`z-[1100]`). Portal pro `<body>` pelo mesmo motivo do [DA-458] — overlay `fixed` so se apoia na
 * tela enquanto nao tiver ancestral com transform/filter.
 */
function SeletorPixTransferencia({ onEscolher, onFechar }: {
  onEscolher: (p: PixParaVincular) => void;
  onFechar: () => void;
}) {
  const [lista, setLista] = useState<PixParaVincular[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api.get<PixParaVincular[]>('/api/mercadopago/recebimentos-para-transferencia')
      .then((r) => { if (vivo) setLista(r); })
      .catch((e: any) => { if (vivo) setErro(e?.message ?? 'Falha ao carregar os Pix'); });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onFechar(); } };
    window.addEventListener('keydown', onEsc, true);
    return () => window.removeEventListener('keydown', onEsc, true);
  }, [onFechar]);

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.45)' }}
    >
      <div
        style={{
          background: 'var(--modal-bg, var(--panel-bg, #fff))',
          borderRadius: 8, width: 'min(560px, 94vw)', maxHeight: '80vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}
      >
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px' }}>
          <strong style={{ fontSize: 15 }}>Qual Pix esta transferencia produziu?</strong>
          <button type="button" className="btn-fechar-modal" onClick={onFechar} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '10px 14px', overflowY: 'auto' }}>
          <p style={{ fontSize: 12, lineHeight: 1.45, marginBottom: 10, opacity: 0.8 }}>
            Sao os Pix que chegaram no Mercado Pago e ainda esperam na Conciliacao Bancaria.
            Escolher um preenche o valor e o destino — sobra so dizer de qual conta o dinheiro saiu.
          </p>

          {lista === null && !erro && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '18px 0', justifyContent: 'center', opacity: 0.7 }}>
              <Loader2 size={16} className="animate-spin" /> <span style={{ fontSize: 13 }}>Carregando...</span>
            </div>
          )}

          {erro && (
            <div style={{ background: '#f97316', color: '#000', padding: '8px 10px', borderRadius: 4, fontSize: 13 }}>
              {erro}
            </div>
          )}

          {lista?.length === 0 && (
            <div style={{ padding: '18px 4px', fontSize: 13, lineHeight: 1.5 }}>
              Nenhum Pix esperando na Conciliacao Bancaria agora. Se o dinheiro ainda nao caiu no
              Mercado Pago, registre a transferencia sem vincular — da pra amarrar depois.
            </div>
          )}

          {lista?.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onEscolher(p)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', marginBottom: 6,
                padding: '8px 10px', borderRadius: 4, cursor: 'pointer',
                border: '1px solid var(--border)', background: 'var(--input-bg)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{formatMoeda(Number(p.valor))}</span>
                <span style={{ fontSize: 11, opacity: 0.7 }}>
                  {formatDataHoraSP(p.data_pagamento ?? p.criado_em)}
                </span>
              </div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                {p.pagador_banco || p.pagador_nome_exibicao || 'Pagador nao informado'}
                {p.pagador_agencia ? ` · Ag. ${p.pagador_agencia}` : ''}
                {p.pagador_conta ? ` · Conta ${p.pagador_conta}` : ''}
              </div>
            </button>
          ))}
        </div>

        <div style={{ padding: '10px 14px', display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function ModalTransferenciaCaixa({
  contas, origemFixa, origemInicial, onFechar, onConcluido,
}: {
  contas: ContaOpcao[];
  /** Operador sem alcance pra escolher a origem (nao-admin/gerente): a gaveta dele, travada. */
  origemFixa?: { id: number; nome: string } | null;
  origemInicial?: number | null;
  onFechar: () => void;
  onConcluido: () => void;
}) {
  const toast = useToast();
  const aviso = useAviso();
  const [form, setForm] = useState<{ caixa_origem_id: number | ''; caixa_destino_id: number | ''; valor: number; motivo: string }>({
    caixa_origem_id: origemFixa?.id ?? origemInicial ?? '',
    caixa_destino_id: '',
    valor: 0,
    motivo: '',
  });
  const [saldoInsuf, setSaldoInsuf] = useState<SaldoInsuficiente | null>(null);
  const [salvando, setSalvando] = useState(false);
  // [DA-549] Pix da Conciliacao que esta transferencia produziu. `null` = transferencia solta.
  const [pix, setPix] = useState<PixParaVincular | null>(null);
  const [abrirSeletor, setAbrirSeletor] = useState(false);
  // Saldo da conta de ORIGEM pelo numero da TRAVA (nao o do painel Financeiro — eles divergem).
  const [saldoOrigem, setSaldoOrigem] = useState<number | null>(null);
  const [carregandoSaldo, setCarregandoSaldo] = useState(false);

  const { usuario } = useAuth();
  // Mover dinheiro DIRETO entre contas bancarias nao passa pela fila de confirmacao, entao a
  // permissao e a unica trava — mesmo nivel de quem baixa um transito. O backend cobra de novo.
  const podeEntreContas = ['admin', 'gerente'].includes(usuario?.perfil ?? '');

  const especie = contasDeEspecie(contas);
  const bancarias = contas.filter(ehContaBancaria);
  // Com um Pix amarrado a origem so pode ser conta BANCARIA: quem guarda cedula nao produz Pix na
  // conta do Mercado Pago, e o par tem que dar `imediata` (o backend recusa o resto).
  const origensEspecie = pix ? [] : especie;
  const origensBancarias = podeEntreContas ? bancarias : [];
  // Destino: tudo, tirando a origem ja escolhida.
  const destinosEspecie = especie.filter((c) => c.id !== form.caixa_origem_id);
  const destinosBancarios = bancarias.filter((c) => c.id !== form.caixa_origem_id);

  // Todo Pix da Conciliacao caiu no Mercado Pago — e a conta que a integracao le. Identificada pelo
  // NOME, com o tipo 'digital' como rede: mesma escolha proposital ja feita pro Cofre, que tambem
  // so se identifica assim. Sem ela resolvida, o Vincular nao aparece (em vez de travar num destino
  // errado).
  const contaMercadoPago = contas.find((c) => c.nome === 'Mercado Pago') ?? contas.find((c) => c.tipo === 'digital');
  const podeVincularPix = podeEntreContas && !origemFixa && !!contaMercadoPago;

  // Le o saldo da origem a cada troca. Guarda de corrida (`reqId`): trocar de conta rapido lanca
  // duas consultas, e a que responder por ultimo nao e necessariamente a mais nova — a tela ficaria
  // coerentemente errada, mostrando o saldo da conta anterior sob o nome da atual.
  const reqSaldo = useRef(0);
  useEffect(() => {
    const origemId = form.caixa_origem_id;
    if (!origemId) { setSaldoOrigem(null); setCarregandoSaldo(false); return; }
    const req = ++reqSaldo.current;
    setCarregandoSaldo(true);
    api.get<{ saldo: number }>(`/api/caixa/saldo-para-saida/${origemId}`)
      .then((r) => { if (req === reqSaldo.current) { setSaldoOrigem(Number(r.saldo)); setCarregandoSaldo(false); } })
      .catch(() => { if (req === reqSaldo.current) { setSaldoOrigem(null); setCarregandoSaldo(false); } });
  }, [form.caixa_origem_id]);

  function escolherPix(p: PixParaVincular) {
    setPix(p);
    setAbrirSeletor(false);
    setForm((s) => ({
      ...s,
      valor: Number(p.valor),
      caixa_destino_id: contaMercadoPago?.id ?? s.caixa_destino_id,
      // Origem em especie deixa de ser valida com Pix amarrado — limpa em vez de deixar o
      // operador descobrir no clique do Transferir.
      caixa_origem_id: s.caixa_origem_id && especie.some((c) => c.id === s.caixa_origem_id) ? '' : s.caixa_origem_id,
    }));
  }

  function desvincularPix() {
    setPix(null);
    setForm((s) => ({ ...s, valor: 0 }));
  }

  const contaOrigem = contas.find((c) => c.id === form.caixa_origem_id);
  const contaDestino = contas.find((c) => c.id === form.caixa_destino_id);
  const imediata = ehImediata(contaOrigem, contaDestino);
  // Deposito/saque: uma perna em especie e a outra numa conta — alguem carrega o dinheiro.
  const emTransitoComBanco = !!contaOrigem && !!contaDestino && !imediata
    && (ehContaBancaria(contaOrigem) || ehContaBancaria(contaDestino));

  // O que ainda falta pra poder transferir — `null` quando pode. Vale pra DESABILITAR o botao e pra
  // ESCREVER ao lado dele o motivo: botao apagado e mudo e beco sem saida, nao interface limpa.
  //
  // O saldo entra aqui de proposito: a trava do backend e' quem manda (ela recusa com 409 e o modal
  // bloqueante), mas descobrir a recusa DEPOIS do clique e' o que fazia "saldo insuficiente" chegar
  // como surpresa. Aqui a tela nao inventa regra nova — ela antecipa a mesma, com o mesmo numero.
  const faltaParaTransferir: string | null = (() => {
    if (!form.caixa_origem_id) return 'Escolha de qual conta o dinheiro saiu.';
    if (!form.caixa_destino_id) return 'Escolha a conta de destino.';
    if (!(form.valor > 0)) return 'Informe o valor.';
    if (saldoOrigem !== null && Math.round(form.valor * 100) > Math.round(saldoOrigem * 100)) {
      return `${contaOrigem?.nome} tem ${formatMoeda(saldoOrigem)} — faltam ${formatMoeda(form.valor - saldoOrigem)}.`;
    }
    return null;
  })();

  async function confirmar() {
    if (!form.caixa_origem_id || !form.caixa_destino_id || form.valor <= 0) {
      aviso.erro('Origem, destino e valor obrigatorios'); return;
    }
    setSalvando(true);
    try {
      await api.post('/api/caixa/transferencia', { ...form, mp_recebimento_id: pix?.id ?? null });
      // O texto tem que dizer o que de fato aconteceu com o saldo: a imediata ja moveu as duas
      // contas; a em transito debitou a origem e deixou o destino esperando confirmacao.
      toast.sucesso(
        pix ? 'Transferencia concluida — o Pix saiu da fila da Conciliacao'
          : imediata ? 'Transferencia concluida'
            : 'Transferencia registrada — aguarda confirmacao',
      );
      onConcluido();
      onFechar();
    } catch (e: any) {
      const saldo = lerSaldoInsuficiente(e);
      if (saldo) { setSaldoInsuf(saldo); return; }
      aviso.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <Modal aberto onFechar={onFechar}
        titulo="Transferencia entre caixas"
        largura={620}
        rodape={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            {/* Botao desabilitado DIZ por que — beco sem saida cinza e mudo ja custou uma sessao. */}
            <span style={{ fontSize: 12, opacity: 0.8, textAlign: 'left' }}>{faltaParaTransferir ?? ''}</span>
            <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
              <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
              <button
                type="button"
                className="btn-primario"
                onClick={confirmar}
                disabled={salvando || faltaParaTransferir !== null}
              >
                Transferir
              </button>
            </div>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: 14 }}>
          {/* O PAR DE CONTAS lado a lado com a seta: a transferencia e um movimento de A pra B, e
              empilhar os dois campos escondia isso. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'start' }}>
          <div>
            <label style={rotuloCampo}>De (origem)</label>
            {origemFixa ? (
              <div style={{ padding: '6px 8px', borderRadius: 4, fontSize: 13, border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text-muted)' }}>
                {origemFixa.nome}
              </div>
            ) : (
              <select
                value={form.caixa_origem_id}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : '';
                  // Escolher como origem a conta que ja estava no destino deixaria os dois iguais
                  // (o backend recusa com 400). Limpa o destino em vez de deixar o operador
                  // descobrir no clique do Transferir.
                  setForm((s) => ({ ...s, caixa_origem_id: id, caixa_destino_id: s.caixa_destino_id === id ? '' : s.caixa_destino_id }));
                }}
                style={campo}
              >
                <option value="">Selecione...</option>
                {origensEspecie.length > 0 && (
                  <optgroup label="Dinheiro">
                    {origensEspecie.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </optgroup>
                )}
                {origensBancarias.length > 0 && (
                  <optgroup label="Contas">
                    {origensBancarias.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </optgroup>
                )}
              </select>
            )}

            {/* SALDO DA ORIGEM. O numero e o da TRAVA anti-negativo, nao o do painel Financeiro —
                hoje os dois divergem em varias contas, e mostrar o do painel seria prometer o que o
                servidor recusa. Com ele a vista, "saldo insuficiente" para de chegar como surpresa
                depois do clique. */}
            {form.caixa_origem_id !== '' && (
              <div style={{ marginTop: 4, fontSize: 12 }}>
                {carregandoSaldo ? (
                  <span style={{ opacity: 0.6 }}>lendo o saldo...</span>
                ) : saldoOrigem === null ? (
                  <span style={{ opacity: 0.6 }}>saldo indisponivel</span>
                ) : saldoOrigem < 0 ? (
                  // Conta NEGATIVA no sistema nao pode ler igual a conta saudavel numa tela de
                  // dinheiro. Laranja porque e ATENCAO, nao erro (vermelho fica pro que quebrou; e
                  // verde nunca — daltonismo). Fundo solido + letra preta, nao tint lavado.
                  //
                  // Nao e alarme que grita atoa ([DA-410]): so aparece na conta que ESTA negativa,
                  // so quando ela e escolhida como origem, e explica por que o botao esta apagado.
                  <span style={{
                    display: 'inline-block', background: '#f97316', color: '#000',
                    padding: '2px 6px', borderRadius: 3, fontSize: 11.5, lineHeight: 1.35,
                  }}>
                    conta negativa no sistema:{' '}
                    <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoeda(saldoOrigem)}</strong>
                    {' '}— nao da pra tirar dinheiro dela ate a entrada que falta ser lancada.
                  </span>
                ) : (
                  <span style={{ opacity: 0.85 }}>
                    disponivel <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoeda(saldoOrigem)}</strong>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* A seta desce a altura do rotulo pra ficar na linha dos campos, nao acima deles. */}
          <div style={{ display: 'flex', alignItems: 'center', height: 34, marginTop: 16, opacity: 0.55 }}>
            <ArrowRight size={18} />
          </div>

          <div>
            <label style={rotuloCampo}>Para (destino)</label>
            {pix ? (
              // Destino travado: todo Pix da Conciliacao caiu no Mercado Pago. Campo que nao aceita
              // escolha nao finge ser um seletor. `--input-fg` porque a regra `!important` global so
              // alcanca input/select/textarea — uma <div> nao herda a cor e sairia cinza.
              <div style={{
                padding: '8px 14px', borderRadius: 4, fontSize: 13,
                border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--input-fg)',
              }}>
                {contaMercadoPago?.nome}
              </div>
            ) : (
              <select
                value={form.caixa_destino_id}
                onChange={(e) => setForm((s) => ({ ...s, caixa_destino_id: e.target.value ? Number(e.target.value) : '' }))}
                style={campo}
              >
                <option value="">Selecione...</option>
                {destinosEspecie.length > 0 && (
                  <optgroup label="Dinheiro">
                    {destinosEspecie.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </optgroup>
                )}
                {destinosBancarios.length > 0 && (
                  <optgroup label="Contas">
                    {destinosBancarios.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </optgroup>
                )}
              </select>
            )}
          </div>
          </div>

          {/* O QUE VAI ACONTECER COM O SALDO. Era um cinza de 12px com 75% de opacidade — a
              informacao que decide se alguem precisa confirmar depois nao pode ser a mais apagada
              da tela. Fundo solido + letra preta, e laranja porque "espera confirmacao" e atencao,
              nao erro (vermelho fica so pro que quebrou; e verde nunca — daltonismo). */}
          {(imediata || emTransitoComBanco) && (
            <div style={{
              padding: '8px 10px', borderRadius: 4, fontSize: 12.5, lineHeight: 1.45,
              background: imediata ? '#fde68a' : '#f97316', color: '#000',
            }}>
              {imediata ? (
                <>
                  <strong>Imediata.</strong> Sai de {contaOrigem?.nome} e entra em {contaDestino?.nome} na
                  hora, sem confirmacao. Registre depois de o Pix/TED ja ter caido.
                </>
              ) : (
                <>
                  <strong>{ehContaBancaria(contaDestino!) ? 'Deposito' : 'Saque'} — fica em transito.</strong>{' '}
                  O valor sai de {contaOrigem?.nome} agora e so entra em {contaDestino?.nome} quando
                  alguem confirmar o recebimento (Caixa &rarr; Transferencias).
                </>
              )}
            </div>
          )}

          <div>
            <label style={rotuloCampo}>Valor</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              {pix ? (
                // Valor VIRA ESPELHO do recebimento. Editavel, o vinculo afirmaria uma transferencia
                // que nao corresponde ao dinheiro que entrou — o backend recusa, e a tela nao deve
                // nem deixar chegar la.
                <div style={{
                  flex: 1, padding: '8px 14px', borderRadius: 4,
                  border: '1px solid var(--input-border)', background: 'var(--input-bg)',
                  color: 'var(--input-fg)', fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                }}>
                  {formatMoeda(form.valor)}
                </div>
              ) : (
                <MoneyInput
                  valor={form.valor}
                  onChangeValor={(v) => setForm((s) => ({ ...s, valor: v }))}
                  style={{ flex: 1, fontSize: 17, fontWeight: 700 }}
                />
              )}

              {podeVincularPix && !pix && (
                // Acao primaria dentro de modal e botao SOLIDO, nao link cinza ([DA-548] p.11 — a 4a
                // vez do "esta muito discreto"). `nowrap` porque item de flex encolhe ate quebrar o
                // rotulo em duas linhas.
                <button
                  type="button"
                  className="btn-primario"
                  onClick={() => setAbrirSeletor(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flex: '0 0 auto' }}
                  title="Amarrar esta transferencia ao Pix que ela produziu na Conciliacao Bancaria"
                >
                  <Link2 size={15} /> Vincular
                </button>
              )}
            </div>

            {pix && (
              // O Pix amarrado fica ESCRITO na tela, nao num tooltip: texto que so aparece no hover
              // nao existe pra quem le ([DA-548] p.8).
              <div style={{
                marginTop: 6, padding: '8px 10px', borderRadius: 4, fontSize: 12, lineHeight: 1.45,
                background: '#fde68a', color: '#000',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
              }}>
                <span>
                  <strong>Pix vinculado</strong> — {formatDataHoraSP(pix.data_pagamento ?? pix.criado_em)}
                  {pix.pagador_banco ? ` · ${pix.pagador_banco}` : ''}
                  {pix.pagador_agencia ? ` · Ag. ${pix.pagador_agencia}` : ''}
                  {pix.pagador_conta ? ` · Conta ${pix.pagador_conta}` : ''}
                  <br />
                  Ao concluir, ele sai da fila da Conciliacao Bancaria com esta transferencia escrita na linha.
                </span>
                <button
                  type="button"
                  onClick={desvincularPix}
                  title="Desfazer o vinculo"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#000', flex: '0 0 auto', padding: 2 }}
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {podeVincularPix && !pix && (
              <div style={{ marginTop: 6, fontSize: 11.5, lineHeight: 1.4, opacity: 0.75 }}>
                Mandou dinheiro de uma conta da loja pra outra e o Pix apareceu na Conciliacao?
                Use o Vincular — ele tira o Pix da fila e deixa registrado que nao era dinheiro de cliente.
              </div>
            )}
          </div>

          <div>
            <label style={rotuloCampo}>Motivo</label>
            <input
              value={form.motivo}
              onChange={(e) => setForm((s) => ({ ...s, motivo: e.target.value }))}
              style={campo}
              placeholder="Opcional"
            />
          </div>
        </div>
      </Modal>
      {abrirSeletor && (
        <SeletorPixTransferencia onEscolher={escolherPix} onFechar={() => setAbrirSeletor(false)} />
      )}
      {saldoInsuf && <SaldoInsuficienteModal info={saldoInsuf} onFechar={() => setSaldoInsuf(null)} />}
    </>
  );
}
